/**
 * personalised-mode.js
 * 
 * Early bootstrap file for the personalised mode system.
 * Loaded in <head> without defer to ensure availability of core mode functions.
 * 
 * This file:
 * 1. Ensures the window.personalised namespace exists
 * 2. Provides a safe placeholder for ensureModeInitialized() that works before personalised-utils.js loads
 * 3. Creates an idempotent implementation that only runs once
 * 4. Sets up proper promise-based interface for mode initialization
 * 5. Safely allows overwriting when the full implementation loads
 */

(function() {
  'use strict';
  
  // Skip if this bootstrap has already run
  if (window.personalisedModeBootstrapped) {
    return;
  }
  
  // Ensure the window.personalised namespace exists
  window.personalised = window.personalised || {};
  window.personalised.utils = window.personalised.utils || {};
  
  // Global debug flag for mode-related logs
  window.personalised.debugMode = true;
  
  if (window.personalised.debugMode) {
    console.debug('[Personalised Mode] Early bootstrap initializing');
    console.info('[Mode] personalised-mode.js loaded — initMode is available');
  }
  
  // Only proceed if initMode is not already defined
  // This prevents overwriting any existing implementation
  if (typeof window.personalised.utils.initMode === 'function') {
    if (window.personalised.debugMode) console.debug('[Personalised Mode] initMode already defined, skipping bootstrap');
    return;
  }
  
  // Internal state to ensure idempotent behavior
  var modeInitializationPromise = null;
  
  /**
   * Placeholder implementation of initMode
   * Returns a promise that resolves once mode is initialized
   * If personalised-utils.js loads later, this will be replaced with the full implementation
   * @returns {Promise<string>} Promise resolving to the current mode
   */
  // Create the placeholder function and store a reference we can use for comparison later
  const placeholderInitMode = async function placeholderInitMode() {
    // If we've already initialized once, return the cached promise
    if (modeInitializationPromise) {
      return modeInitializationPromise;
    }
    
    if (window.personalised.debugMode) console.debug('[Personalised Mode] Placeholder ensureModeInitialized called');
    
    // Create a promise that resolves once we've determined if we need to use
    // the placeholder or if the real implementation has loaded
    modeInitializationPromise = new Promise(function(resolve) {
      // If DOM is already loaded, check immediately
      if (document.readyState === 'complete' || document.readyState === 'interactive') {
        checkAndInitialize(resolve);
      } else {
        // Otherwise wait for DOMContentLoaded
        document.addEventListener('DOMContentLoaded', function() {
          checkAndInitialize(resolve);
        });
      }
    });
    
    return modeInitializationPromise;
  };
  
  // Assign the placeholder to the expected location
  window.personalised.utils.initMode = placeholderInitMode;
  
  /**
   * Helper function to check if the real implementation is available
   * and initialize mode appropriately
   * @param {Function} resolve - Promise resolve function
   */
  function checkAndInitialize(resolve) {
    // Check if the real implementation has been loaded and replaced our placeholder
    // We do this by checking if the current function reference differs from our saved reference
    if (window.personalised.utils.initMode !== placeholderInitMode) {
      if (window.personalised.debugMode) console.debug('[Personalised Mode] Real implementation detected, using that instead');
      
      // Call the real implementation and pass through its result
      window.personalised.utils.initMode().then(resolve);
    } else {
      // The real implementation still hasn't loaded, use our fallback
      if (window.personalised.debugMode) console.warn('[Personalised Mode] personalised-utils.js not loaded yet, using fallback mode implementation');
      
      // Set a mock current mode function if it doesn't exist
      if (typeof window.personalised.utils.getMode !== 'function') {
        window.personalised.utils.getMode = function() {
          if (window.personalised.debugMode) console.debug('[Personalised Mode] Using fallback getMode');
          return 'guest';
        };
      }
      
      // Resolve with fallback mode 'guest'
      resolve('guest');
    }
  }
  
  // Mark bootstrap as complete to prevent running twice
  window.personalisedModeBootstrapped = true;
  
  if (window.personalised.debugMode) console.debug('[Personalised Mode] Early bootstrap complete');
})();
